/**
 * Situación de una orden enviada a un actuador.
 *
 * Enviar una orden no es instantáneo: viaja al nodo, este la aplica y devuelve
 * una confirmación. Estos estados describen ese trayecto para que el operador
 * sepa siempre si su orden llegó, y no tenga que deducirlo del silencio.
 */
export const ESTADOS_COMANDO = [
  'inactivo',
  'encolado',
  'pendiente',
  'aplicado',
  'rechazado',
  'sin_respuesta',
  'error',
] as const;

export type EstadoComandoValor = (typeof ESTADOS_COMANDO)[number];

const ETIQUETA: Readonly<Record<EstadoComandoValor, string>> = {
  inactivo: 'Sin órdenes recientes',
  encolado: 'Enviando…',
  pendiente: 'Esperando confirmación…',
  aplicado: 'Aplicado',
  rechazado: 'Rechazado por el equipo',
  // No es un fallo confirmado: es que no sabemos. El equipo puede haberla
  // aplicado y estar tardando en responder.
  sin_respuesta: 'Sin confirmar todavía',
  error: 'No se pudo enviar',
};

const TOKEN: Readonly<Record<EstadoComandoValor, string>> = {
  inactivo: '--text-3',
  encolado: '--status-info',
  pendiente: '--status-warning',
  aplicado: '--status-success',
  rechazado: '--status-danger',
  // Ámbar y no rojo: rojo es «no se hizo», y eso no es lo que sabemos.
  sin_respuesta: '--status-warning',
  error: '--status-danger',
};

export class EstadoComando {
  private constructor(readonly valor: EstadoComandoValor) {}

  static from(valor: EstadoComandoValor): EstadoComando {
    return new EstadoComando(valor);
  }

  static inactivo(): EstadoComando {
    return new EstadoComando('inactivo');
  }

  get etiqueta(): string {
    return ETIQUETA[this.valor];
  }

  get color(): string {
    return `var(${TOKEN[this.valor]})`;
  }

  /** La orden está viajando: no conviene enviar otra al mismo actuador. */
  get enCurso(): boolean {
    return this.valor === 'encolado' || this.valor === 'pendiente';
  }

  /**
   * Algo salió mal de forma comprobada.
   *
   * «Sin confirmar» no entra: significa que no llegó confirmación a tiempo, no
   * que la orden fallara. Contarlo como fallo hacía que la pantalla afirmara
   * que no se hizo algo que muchas veces sí se había hecho.
   */
  get fallo(): boolean {
    return this.valor === 'rechazado' || this.valor === 'error';
  }

  /** Ni confirmada ni fallida: sigue sin saberse. */
  get incierto(): boolean {
    return this.valor === 'sin_respuesta';
  }

  toString(): string {
    return this.valor;
  }
}
